import React from 'react';
import { cn } from '@/lib/utils';
import type { OptionConfig } from './types';
import AnimatedIcon from '@/components/ui/AnimatedIcon';

interface OptionCardProps {
  option: OptionConfig;
  selected: boolean;
  multiSelect?: boolean;
  onSelect: (value: string) => void;
  disabled?: boolean;
}

const OptionCard: React.FC<OptionCardProps> = ({
  option,
  selected,
  multiSelect = false,
  onSelect,
  disabled = false,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) onSelect(option.value);
    }
  };

  return (
    <div
      role={multiSelect ? 'checkbox' : 'radio'}
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onSelect(option.value)}
      onKeyDown={handleKeyDown}
      className={cn(
        // Base
        'relative group flex items-start gap-4 p-5 rounded-2xl border-2 cursor-pointer',
        'transition-all duration-200 ease-out select-none outline-none',
        // Focus ring
        'focus-visible:ring-2 focus-visible:ring-[#1A412D] focus-visible:ring-offset-2',
        // Default state
        !selected && !disabled && [
          'bg-white border-[#E8E2D9] hover:border-[#1A412D]/40',
          'hover:bg-[#F8F5F1] hover:shadow-md hover:-translate-y-0.5',
        ],
        // Selected state
        selected && [
          'bg-[#1A412D] border-[#1A412D] text-white shadow-lg',
          '-translate-y-0.5 shadow-[#1A412D]/20',
        ],
        // Disabled state
        disabled && 'opacity-40 cursor-not-allowed hover:translate-y-0 hover:shadow-none',
      )}
    >
      {/* Selection indicator */}
      <div
        className={cn(
          'absolute top-4 right-4 shrink-0',
          'w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center',
          multiSelect ? 'rounded-md' : 'rounded-full',
          selected
            ? 'bg-[#B8883D] border-[#B8883D]'
            : 'bg-transparent border-[#C4BAB0] group-hover:border-[#1A412D]/60',
        )}
        aria-hidden="true"
      >
        {selected && (
          <svg
            viewBox="0 0 12 12"
            fill="none"
            className="w-3 h-3"
            aria-hidden="true"
          >
            <path
              d={multiSelect ? 'M2 6l3 3 5-5' : 'M6 2a4 4 0 100 8A4 4 0 006 2z'}
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill={multiSelect ? 'none' : 'white'}
            />
          </svg>
        )}
      </div>

      {/* Icon */}
      {option.icon && (
        <AnimatedIcon
          name={option.icon}
          size={32}
          animation="scale"
          className="shrink-0 mt-0.5"
          color={selected ? 'white' : '#B8883D'}
        />
      )}

      {/* Text */}
      <div className="flex-1 min-w-0 pr-6">
        <p
          className={cn(
            'font-sans font-semibold text-sm leading-snug transition-colors',
            selected ? 'text-white' : 'text-[#1A412D]',
          )}
        >
          {option.label}
        </p>
        {option.description && (
          <p
            className={cn(
              'font-sans text-xs mt-1 leading-relaxed transition-colors',
              selected ? 'text-white/70' : 'text-[#7A7367]',
            )}
          >
            {option.description}
          </p>
        )}
      </div>
    </div>
  );
};

export default OptionCard;
